import {
  ButtonDS,
  EditOutlined,
  Spacer,
  Switch,
  TextCapitalized,
  dateFormatterDayMonthYearLong2Digits,
} from "@qivia/ui";
import { SidePanelDS } from "@qivia/ui";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ManagerDataResponse } from "../usersAPI";
import styled from "styled-components";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import "../../../../i18n/i18n";
import { updateUserMileageGapReportsAsync } from "../usersSlice";
import { useAppDispatch } from "../../../../redux/hooks";

export const SidePanelUserDetails = (props: {
  manager: ManagerDataResponse;
  canUpdateRole: boolean;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isActiveManager = useMemo(() => {
    return props.manager.status === "ACTIVE";
  }, [props.manager.status]);

  const onUpdateRole = useCallback(() => {
    if (props.manager.uuid) {
      navigate(`/home/user_update_role/${props.manager.uuid}`);
    }
  }, [navigate, props.manager.uuid]);

  const invitationDate = useMemo(() => {
    if (!props.manager.requestedAt) {
      return null;
    }
    return dateFormatterDayMonthYearLong2Digits(
      new Date(props.manager.requestedAt),
    );
  }, [props.manager.requestedAt]);

  const onUpdateReceiveMileageGapReports = useCallback(() => {
    if (props.manager.uuid) {
      void dispatch(updateUserMileageGapReportsAsync({
        userUuid: props.manager.uuid,
        receiveMileageGapReports: !props.manager.receiveMileageGapReports }));
    }
  }, [dispatch, props.manager.receiveMileageGapReports, props.manager.uuid]);

  return (
    <>
      <TextCapitalized>
        <StyledSectionTitle>
          {t("users.management.sidePanel.details.title")}
        </StyledSectionTitle>
      </TextCapitalized>
      <Spacer y={2} />
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t("users.management.sidePanel.details.requestedAt")}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={invitationDate || "—"}
      />
      <Spacer y={1} />
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t("users.management.sidePanel.details.role")}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={
          <StyledRoleRow>
            <TextCapitalized>
              {t(`users.management.sidePanel.role.${props.manager.role}.title`)}
            </TextCapitalized>
            {props.canUpdateRole && isActiveManager ? (
              <>
                <Spacer x={0.5} />
                <ButtonDS
                  singleIcon={{ icon: <EditOutlined />, size: "S" }}
                  format="hug"
                  buttonType="tertiary"
                  onClick={onUpdateRole}
                />
              </>
            ) : null}
          </StyledRoleRow>
        }
      />
      <Spacer y={1} />
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t("users.management.sidePanel.details.job")}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={props.manager.job || "—"}
      />
      <Spacer y={1.5} />
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t("users.management.sidePanel.details.receiveMileageGapReports")}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={
          <Switch
            checked={props.manager.receiveMileageGapReports}
            onClick={onUpdateReceiveMileageGapReports}
            size={"S"}
            disabled={!isActiveManager}
          />
        }
      />
    </>
  );
};

const SectionRowLabel = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/black"]}
`;

const StyledSectionTitle = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledRoleRow = styled.div`
  display: flex;
  align-items: center;
  min-height: 2.5rem;
`;
